import { useState, useEffect } from 'react';
import { updateProfile, updatePassword, reauthenticateWithCredential, EmailAuthProvider, linkWithPopup, unlink } from 'firebase/auth';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { auth, db, googleProvider } from '../firebase/config';
import Modal from '../components/Modal';

function AlertIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="profile-alert-icon">
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  );
}

function getInitials(name) {
  if (!name) return '?';
  return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
}

export default function Profile({ user }) {
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [createdAt, setCreatedAt] = useState(null);
  const [isGoogleLinked, setIsGoogleLinked] = useState(false);
  const [photoURL, setPhotoURL] = useState(null);

  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');

  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [pwLoading, setPwLoading] = useState(false);
  const [pwError, setPwError] = useState('');

  const [showDisconnectModal, setShowDisconnectModal] = useState(false);
  const [disconnectLoading, setDisconnectLoading] = useState(false);

  useEffect(() => {
    if (!user) return;
    setEmail(user.email || '');
    setDisplayName(user.displayName || '');
    setPhotoURL(user.photoURL || null);

    const linked = user.providerData?.some(p => p.providerId === 'google.com');
    setIsGoogleLinked(linked);

    const loadFirestoreData = async () => {
      try {
        const snap = await getDoc(doc(db, 'users', user.uid));
        if (snap.exists()) {
          const data = snap.data();
          setCreatedAt(data.createdAt?.toDate?.() || null);
          if (!user.displayName && data.displayName) {
            setDisplayName(data.displayName);
          }
        }
      } catch (err) {
        console.error('Failed to load user data:', err);
      }
    };

    loadFirestoreData();
  }, [user]);

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setSaveMsg('');
    setSaving(true);

    try {
      await updateProfile(auth.currentUser, { displayName: displayName.trim() });
      await updateDoc(doc(db, 'users', user.uid), { displayName: displayName.trim() });
      setSaveMsg('Profile updated successfully.');
    } catch (err) {
      setSaveMsg('Failed to update profile.');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPwError('');
    setPwLoading(true);

    try {
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(auth.currentUser, credential);
      await updatePassword(auth.currentUser, newPassword);
      setShowPasswordModal(false);
      setNewPassword('');
      setCurrentPassword('');
      setSaveMsg('Password changed successfully.');
    } catch (err) {
      if (err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
        setPwError('Current password is incorrect.');
      } else if (err.code === 'auth/weak-password') {
        setPwError('New password is too weak.');
      } else {
        setPwError('Failed to change password.');
      }
    } finally {
      setPwLoading(false);
    }
  };

  const handleConnectGoogle = async () => {
    try {
      await linkWithPopup(auth.currentUser, googleProvider);
      setIsGoogleLinked(true);
    } catch (err) {
      if (err.code !== 'auth/popup-closed-by-user') {
        console.error('Failed to link Google:', err);
      }
    }
  };

  const handleDisconnectGoogle = async () => {
    setDisconnectLoading(true);
    try {
      await unlink(auth.currentUser, 'google.com');
      setIsGoogleLinked(false);
      setShowDisconnectModal(false);
    } catch (err) {
      console.error('Failed to disconnect Google:', err);
    } finally {
      setDisconnectLoading(false);
    }
  };

  return (
    <div className="profile-page">
      <div className="profile-card">

        {/* Header */}
        <div className="profile-header">
          <div className="profile-avatar">
            {photoURL
              ? <img src={photoURL} alt={displayName} className="profile-avatar-img" />
              : <span className="profile-avatar-text">{getInitials(displayName || email)}</span>
            }
          </div>
          <div className="profile-header-info">
          <h1 className="profile-header-name">{displayName || 'No name set'}</h1>
          <p className="profile-header-email">{email}</p>
        </div>
        </div>

        {/* Success/Error message */}
        {saveMsg && (
          <div className={`profile-toast ${saveMsg.includes('Failed') ? 'profile-toast--error' : 'profile-toast--success'}`}>
            {saveMsg}
          </div>
        )}

        {/* Edit Display Name */}
        <div className="profile-section">
          <h2 className="profile-section-title">Display Name</h2>
          <form onSubmit={handleSaveProfile} className="profile-edit-row">
            <input
              type="text"
              value={displayName}
              onChange={(e) => { setDisplayName(e.target.value); setSaveMsg(''); }}
              className="auth-input profile-edit-input"
              placeholder="Your name"
            />
            <button type="submit" disabled={saving} className="profile-save-btn">
              {saving ? 'Saving...' : 'Save'}
            </button>
          </form>
        </div>

        {/* Account Info */}
        <div className="profile-section">
          <h2 className="profile-section-title">Account</h2>
          <div className="profile-info-grid">
            <div className="profile-info-row">
              <span className="profile-info-label">Email</span>
              <span className="profile-info-value">{email}</span>
            </div>
            {createdAt && (
              <div className="profile-info-row">
                <span className="profile-info-label">Joined</span>
                <span className="profile-info-value">{createdAt.toLocaleDateString()}</span>
              </div>
            )}
            <div className="profile-info-row">
              <span className="profile-info-label">Google</span>
              <span className={`profile-info-value ${isGoogleLinked ? 'profile-info-value--green' : ''}`}>
                {isGoogleLinked ? 'Connected' : 'Not linked'}
              </span>
            </div>
          </div>
        </div>

        {/* Security */}
        <div className="profile-section">
          <h2 className="profile-section-title">Security</h2>
          <div className="profile-actions">

            {!isGoogleLinked && (
              <button onClick={handleConnectGoogle} className="auth-google-btn">
                <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Connect Google Account
              </button>
            )}

            {isGoogleLinked && (
              <button onClick={() => setShowDisconnectModal(true)} className="profile-action-btn">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="15" y1="9" x2="9" y2="15"/>
                  <line x1="9" y1="9" x2="15" y2="15"/>
                </svg>
                Disconnect Google Account
              </button>
            )}

            <button onClick={() => setShowPasswordModal(true)} className="profile-action-btn">
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <rect x="3" y="11" width="18" height="11" rx="2"/>
                <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
              </svg>
              Change Password
            </button>

          </div>
        </div>

      </div>

      {/* Password Modal */}
      <Modal isOpen={showPasswordModal} onClose={() => { setShowPasswordModal(false); setNewPassword(''); setCurrentPassword(''); setPwError(''); }} title="Change Password">
        {pwError && (
          <div className="auth-error" style={{ marginBottom: '1rem' }}>
            <AlertIcon />
            <span>{pwError}</span>
          </div>
        )}
        <form onSubmit={handleChangePassword}>
          <div className="auth-input-group">
            <label className="auth-label">Current Password</label>
            <input type="password" className="auth-input" placeholder="Current password" value={currentPassword} onChange={(e) => { setCurrentPassword(e.target.value); setPwError(''); }} required autoFocus />
          </div>
          <div className="auth-input-group">
            <label className="auth-label">New Password</label>
            <input type="password" className="auth-input" placeholder="New password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required minLength={6} />
          </div>
          <button type="submit" disabled={pwLoading} className="auth-button" style={{ marginTop: '0.25rem' }}>
            {pwLoading ? 'Changing...' : 'Change Password'}
          </button>
        </form>
      </Modal>

      {/* Disconnect Google Modal */}
      <Modal isOpen={showDisconnectModal} onClose={() => setShowDisconnectModal(false)} title="Disconnect Google">
        <p className="profile-modal-text">
          Are you sure? You will no longer be able to sign in with Google. You can reconnect anytime.
        </p>
        <button onClick={handleDisconnectGoogle} disabled={disconnectLoading} className="auth-button profile-disconnect-btn">
          {disconnectLoading ? 'Disconnecting...' : 'Disconnect'}
        </button>
      </Modal>
    </div>
  );
}
